import "./BookedRoamingOption/BookedRoamingOption.scss";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ConnectionIcon, VoiceOptionCall } from "@/assets/icons/icons";
import { SectionButtonHeader } from "@/components/Interface/SectionButtonHeader/SectionButtonHeader";
import { CountrylistDialog } from "@/components/Portal/Cockpit/DeviceManagement/Roaming/AddRoamingOption/CountryList/CountryList";
import { RoamingUnlimited } from "@/components/Portal/Cockpit/DeviceManagement/Roaming/RoamingUnlimited/RoamingUnlimited";
import { RoamingUsage } from "@/components/Portal/Cockpit/DeviceManagement/Roaming/ShowRoamingOptions/RoamingUsage/RoamingUsage";
import {
  getSimRoamingCallPackageUsage,
  useGetVoiceSettings,
} from "@/services/api";
import {
  GetRoamingPackageUsageResponse,
  RoamingInfoRoamingStatus,
  RoamingInfoUsageZone,
  SimCardDetail,
} from "@/services/model";
import { getTariffName, getTariffType, TariffTypes } from "@/utils/deviceUtils";
import { getCallsVolume } from "@/utils/tariffUtils";

interface FakeUsedDataRoamingOptionProps {
  simDetail: SimCardDetail;
  isFirstOfUsageZone: boolean;
}

/**
 * This component displays an (faked) booked roaming option.
 *
 * Backend's service does not provide any data once the roaming data volume is
 * fully used. So we fake it using this component.
 *
 */
export const FakeUsedDataRoamingOption = ({
  simDetail,
  isFirstOfUsageZone,
}: FakeUsedDataRoamingOptionProps) => {
  const { t } = useTranslation("portal");
  const [roamingCallPackageUsage, setRoamingCallPackageUsage] =
    useState<GetRoamingPackageUsageResponse>();

  const { data: voiceSettings } = useGetVoiceSettings(
    {
      simId: simDetail.simId || -1,
    },
    {
      query: {
        enabled: true,
      },
    },
  );
  const isRoamingCallsUsageNotAllowed =
    voiceSettings?.reason?.includes("CreditClassC");

  const tariffType = getTariffType(simDetail.serviceID);

  const isMobileOrMobilePlusTariff =
    tariffType === TariffTypes.flatMobile ||
    tariffType === TariffTypes.flatMobilePlus;

  const name = getTariffName(simDetail);

  const callsVolume = getCallsVolume({
    t,
    tariffType,
    minContractDuration: simDetail.minContractDuration,
  });

  const fakeInitialRoamingVolume =
    tariffType === TariffTypes.flatMobile ? "60 GB" : "144 GB";
  const fakeRoamingVolume =
    tariffType === TariffTypes.flatMobile ? 61440 : 147456;

  useEffect(() => {
    if (tariffType === TariffTypes.flatMobile && simDetail.simId) {
      getSimRoamingCallPackageUsage({ simId: simDetail.simId }).then((data) => {
        setRoamingCallPackageUsage({
          roamingCalls: data.roamingCalls
            ? Math.ceil(data.roamingCalls / 60)
            : 0,
          maxInclRoamingCalls: data.maxInclRoamingCalls
            ? data.maxInclRoamingCalls / 60
            : 0,
        });
      });
    }
  }, [simDetail.simId, tariffType]);

  return (
    <div className="roaming-option" data-testid="roaming-option">
      <h3 className="title">{t("mobileAbo.name")}</h3>
      <div className="mb-14">
        <SectionButtonHeader
          className="mb-2 h-6"
          header={t("cockpit.managementTile.roaming.includedInSubscription")}
        >
          <CountrylistDialog />
        </SectionButtonHeader>

        <span className="font-semibold text-primary-100">{name}</span>

        <div className="icon-wrapper">
          <div className="icon-row">
            <ConnectionIcon />
            {fakeInitialRoamingVolume}
          </div>

          <div className="icon-row">
            <>
              <VoiceOptionCall className="size-6 text-primary-100" />
              {callsVolume}
            </>
          </div>
        </div>
      </div>
      <RoamingUsage
        showFlatMobileExplanation={false}
        days={0}
        description="cockpit.managementTile.roaming.showOption.active.remainingData"
        initial={fakeRoamingVolume}
        isFirstOfUsageZone={isFirstOfUsageZone}
        remaining={0}
        consumed={fakeRoamingVolume}
        isIncludedInService={true}
        roamingStatus={RoamingInfoRoamingStatus.active}
        title={t("cockpit.managementTile.roaming.showOption.active.dataUsage")}
        isYearly={true}
        isMobileOrMobilePlusTariff={isMobileOrMobilePlusTariff}
        zone={RoamingInfoUsageZone.ZONE1}
        isDataUsage={true}
      />

      <>
        {tariffType === TariffTypes.flatMobilePlus ? (
          <RoamingUnlimited
            showRoamingCallExplanation={true}
            isNotAllowedToUse={isRoamingCallsUsageNotAllowed}
          />
        ) : (
          <RoamingUsage
            days={0}
            description="cockpit.managementTile.roaming.showOption.active.remainingMin"
            initial={roamingCallPackageUsage?.maxInclRoamingCalls || 0}
            remaining={
              (roamingCallPackageUsage?.maxInclRoamingCalls || 0) -
              (roamingCallPackageUsage?.roamingCalls || 0)
            }
            consumed={roamingCallPackageUsage?.roamingCalls}
            isIncludedInService={true}
            roamingStatus={RoamingInfoRoamingStatus.active}
            title={t(
              "cockpit.managementTile.roaming.showOption.active.callsToAndFromAbroad",
            )}
            isFirstOfUsageZone={isFirstOfUsageZone}
            showRoamingCallExplanation={true}
            isNotAllowedToUse={isRoamingCallsUsageNotAllowed}
            isMobileOrMobilePlusTariff={isMobileOrMobilePlusTariff}
            isRoamingCallType={true}
          />
        )}
      </>
    </div>
  );
};
